/**
 * LeadIntakePanel — staff surface for the Lead Intake Upload Portal
 * (Phase 5i, Jun 11 2026 — Ana spec §8, Daniel's tight-RBAC directive).
 *
 * Mounted on both ContactPEMTab and ContactPhysicianTab. Self-gates on
 * the viewer's effective permissions:
 *   - lead_intake:send            → "Send intake upload link" button + modal
 *                                   + outstanding-links list with revoke
 *   - lead_intake_documents:read  → uploaded documents list with download
 *   - lead_intake_documents:write → mark-reviewed action
 *
 * A standard PEM (send-only) sees the link tools but NOT the uploaded
 * documents — that's Daniel's directive working as designed. Senior PEMs
 * with the per-user override see both.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavStep } from "../lib/NavHistory";
import {
  Link2, FileUp, FileText, CheckCircle2, Copy, XCircle, Loader2, AlertCircle, X, FolderInput,
} from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "lab_work", label: "Lab work / blood panels" },
  { value: "imaging", label: "Imaging (MRI, X-ray, CT, ultrasound)" },
  { value: "prior_records", label: "Prior clinical records" },
  { value: "medication_list", label: "Medication list" },
  { value: "other", label: "Other documents" },
];

function hasPerm(user, p) {
  return new Set(user?.permissions || []).has(p);
}

export default function LeadIntakePanel({ contactId, user }) {
  const canSend = hasPerm(user, "lead_intake:send");
  const canRead = hasPerm(user, "lead_intake_documents:read");
  const canWrite = hasPerm(user, "lead_intake_documents:write");

  const [tokens, setTokens] = useState([]);
  const [docs, setDocs] = useState([]);
  const [showSend, setShowSend] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!contactId || !canRead) return;
    try {
      const r = await window.zignagenixAPI.invoke("lead-intake:list", { contactId });
      if (r && !r.error) {
        setTokens(r.tokens || []);
        setDocs(r.documents || []);
      }
    } catch (_) { /* silent — panel degrades */ }
  }, [contactId, canRead]);

  useEffect(() => { load(); }, [load]);

  const openDoc = useCallback(async (id) => {
    setBusy(id); setErr("");
    try {
      const r = await window.zignagenixAPI.invoke("lead-intake:get-url", { id });
      if (r && r.url) window.open(r.url, "_blank", "noopener");
      else setErr(r?.message || r?.code || "Could not open document");
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  }, []);

  const markReviewed = useCallback(async (id) => {
    setBusy(id);
    try {
      await window.zignagenixAPI.invoke("lead-intake:mark-reviewed", { id });
      await load();
    } catch (_) {} finally { setBusy(null); }
  }, [load]);

  const [converting, setConverting] = useState(false);
  const [convertMsg, setConvertMsg] = useState("");
  const convertToEmr = useCallback(async () => {
    setConverting(true); setConvertMsg(""); setErr("");
    try {
      const r = await window.zignagenixAPI.invoke("lead-intake:convert-to-emr", { contactId });
      if (r && r.error) setErr(r.message || r.code);
      else {
        setConvertMsg(r.converted > 0
          ? `Copied ${r.converted} document${r.converted > 1 ? "s" : ""} to the patient's EMR.`
          : (r.message || "Nothing new to copy."));
        await load();
      }
    } catch (e) { setErr(e.message); } finally { setConverting(false); }
  }, [contactId, load]);

  const revoke = useCallback(async (id) => {
    if (!window.confirm("Revoke this upload link? The lead will no longer be able to use it.")) return;
    setBusy(`t${id}`);
    try {
      await window.zignagenixAPI.invoke("lead-intake:revoke", { id });
      await load();
    } catch (_) {} finally { setBusy(null); }
  }, [load]);

  if (!canSend && !canRead) return null;

  return (
    <div style={{ border: "1px solid #e1e6ed", borderRadius: 6, background: "#fff", padding: 14, marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <FileUp size={15} color="#2E8B98" />
        <strong style={{ fontSize: 13 }}>Lead intake uploads</strong>
        <span style={{ fontSize: 11, color: "#9aa4b2" }}>
          pre-consult records · physician + senior-PEM access
        </span>
        {canSend && (
          <button onClick={() => { setShowSend(true); setLastLink(null); }} style={{ ...btn, marginLeft: "auto" }}>
            <Link2 size={12} /> Send intake upload link
          </button>
        )}
      </div>

      {err && <div style={errBox}><AlertCircle size={13} /> {err}</div>}

      {lastLink && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 10,
          background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, fontSize: 12,
        }}>
          <CheckCircle2 size={14} color="#15803D" />
          <span style={{ color: "#166534" }}>
            {/* ⛔ Card P86: "queued via SMS + email" read as "it is going out".
                The rows are written, but the dispatcher records them blocked
                while the kill switch is off. Same sentence #619 uses. */}
            Link created{lastLink.messagingEnabled === false
              ? ""
              : lastLink.queuedChannels?.length ? ` — queued via ${lastLink.queuedChannels.join(" + ")}` : ""}.
          </span>
          {lastLink.messagingEnabled === false && (
            <span data-testid="lead-intake-not-sent" style={{ color: "#9A3412", fontWeight: 600 }}>
              Not sent yet: texting and email go live with the Twilio integration.
            </span>
          )}
          <code style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
            {lastLink.link}
          </code>
          <button
            onClick={() => navigator.clipboard?.writeText(lastLink.link)}
            style={btnGhost} title="Copy link"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      )}

      {/* Documents — visible only with lead_intake_documents:read */}
      {canRead ? (
        <>
          {/* Convert-to-EMR (Phase 5i follow-up): copy pre-consult uploads
              into the longitudinal record once the lead becomes a patient.
              Server enforces the patient link; here we offer it whenever
              there are docs not yet in the EMR. */}
          {canWrite && docs.some((d) => !d.converted_lab_document_id) && (
            <div style={{ marginBottom: 8 }}>
              <button onClick={convertToEmr} disabled={converting} style={{ ...btn, background: "#1f6feb" }}>
                {converting ? <Loader2 size={12} className="spin" /> : <FolderInput size={12} />}
                Copy to patient EMR
              </button>
              {convertMsg && <span style={{ fontSize: 11, color: "#15803D", marginLeft: 8 }}>{convertMsg}</span>}
            </div>
          )}

          {docs.length === 0 ? (
            <div style={{ fontSize: 12, color: "#9aa4b2", fontStyle: "italic", padding: "6px 0" }}>
              No uploads yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {docs.map((d) => (
                <div key={d.id} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                  border: "1px solid #f0f3f7", borderRadius: 6,
                  background: d.reviewed_at ? "#fafdfb" : "#fffdf5",
                }}>
                  <FileText size={14} color="#6C757D" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {d.original_filename}
                    </div>
                    <div style={{ fontSize: 11, color: "#6C757D" }}>
                      {(d.category || "other").replace(/_/g, " ")} · {(d.size_bytes / (1024 * 1024)).toFixed(1)} MB
                      {" · "}{new Date(d.uploaded_at).toLocaleDateString()}
                      {d.reviewed_at && <span style={{ color: "#15803D" }}> · reviewed{d.reviewed_by_name ? ` by ${d.reviewed_by_name}` : ""}</span>}
                      {d.converted_lab_document_id && <span style={{ color: "#1f6feb", fontWeight: 700 }}> · in EMR</span>}
                    </div>
                  </div>
                  <button onClick={() => openDoc(d.id)} disabled={busy === d.id} style={btn}>
                    {busy === d.id ? <Loader2 size={12} className="spin" /> : "Open"}
                  </button>
                  {canWrite && !d.reviewed_at && (
                    <button onClick={() => markReviewed(d.id)} disabled={busy === d.id} style={btnGhost}>
                      <CheckCircle2 size={12} /> Reviewed
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Outstanding links */}
          {tokens.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                Upload links
              </div>
              {tokens.map((t) => {
                const dead = t.revoked_at || new Date(t.expires_at) < new Date();
                return (
                  <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#5a6478", padding: "3px 0" }}>
                    <span style={{ color: dead ? "#b00020" : "#15803D", fontWeight: 700 }}>
                      {t.revoked_at ? "revoked" : new Date(t.expires_at) < new Date() ? "expired" : "active"}
                    </span>
                    <span>sent {new Date(t.created_at).toLocaleDateString()}{t.created_by_name ? ` by ${t.created_by_name}` : ""}</span>
                    <span>· used {t.use_count || 0}×</span>
                    {canSend && !dead && (
                      <button onClick={() => revoke(t.id)} disabled={busy === `t${t.id}`} style={{ ...btnGhost, padding: "2px 7px", fontSize: 10 }}>
                        <XCircle size={10} /> Revoke
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: 12, color: "#9aa4b2", fontStyle: "italic" }}>
          Uploaded documents are visible to physicians and designated senior PEMs only.
        </div>
      )}

      {showSend && (
        <SendLinkModal
          contactId={contactId}
          onClose={() => setShowSend(false)}
          onSent={async (result) => { setShowSend(false); setLastLink(result); await load(); }}
        />
      )}
    </div>
  );
}

function SendLinkModal({ contactId, onClose, onSent }) {
  // #65 Phase C1: this dialog is parent-mounted (rendered only while open), so
  // it registers a Back step for its whole lifetime — global Back closes it
  // before navigating. Unconditional hook, generic label, never PHI.
  useNavStep({ active: true, label: 'Send link', onBack: () => onClose && onClose() });
  const [categories, setCategories] = useState(CATEGORY_OPTIONS.map((c) => c.value));
  const [expiresDays, setExpiresDays] = useState(30);
  const [instructions, setInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (v) => setCategories((cs) =>
    cs.includes(v) ? cs.filter((c) => c !== v) : [...cs, v]);

  async function submit() {
    if (!categories.length) { setErr("Pick at least one document category."); return; }
    setSaving(true); setErr("");
    try {
      const r = await window.zignagenixAPI.invoke("lead-intake:send-link", {
        contactId, categories, expiresDays, instructions: instructions.trim() || null,
      });
      if (r && r.error) setErr(r.message || r.code);
      else await onSent(r);
    } catch (e) { setErr(e.message || "Failed to create link"); }
    setSaving(false);
  }

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000, background: "rgba(17,24,39,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 8, maxWidth: 440, width: "100%", padding: 18,
        boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Link2 size={15} color="#2E8B98" />
          <strong style={{ fontSize: 14 }}>Send intake upload link</strong>
          <button onClick={onClose} aria-label="Close" style={{ ...btnGhost, marginLeft: "auto", padding: "3px 7px" }}><X size={12} /></button>
        </div>

        <div style={{ fontSize: 11, fontWeight: 600, color: "#3a4a5e", marginBottom: 4 }}>
          Documents to request
        </div>
        {CATEGORY_OPTIONS.map((c) => (
          <label key={c.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={categories.includes(c.value)} onChange={() => toggle(c.value)} />
            {c.label}
          </label>
        ))}

        <div style={{ fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "10px 0 4px" }}>
          Link expires after
        </div>
        <select value={expiresDays} onChange={(e) => setExpiresDays(parseInt(e.target.value, 10))} style={input}>
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days (default)</option>
          <option value={60}>60 days</option>
          <option value={90}>90 days</option>
        </select>

        <div style={{ fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "10px 0 4px" }}>
          Instructions for the lead (optional)
        </div>
        <textarea
          value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2}
          placeholder='e.g. "Please include your January MRI report"'
          style={{ ...input, resize: "vertical" }}
        />

        {err && <div style={errBox}><AlertCircle size={13} /> {err}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} style={btn}>
            {saving ? <><Loader2 size={12} className="spin" /> Creating…</> : "Create + send link"}
          </button>
        </div>
      </div>
    </div>
  );
}

const btn = {
  padding: "5px 12px", fontSize: 12, fontWeight: 600,
  background: "#2E8B98", color: "#fff", border: "none", borderRadius: 6,
  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};
const btnGhost = {
  padding: "5px 10px", fontSize: 12, fontWeight: 600,
  background: "#fff", color: "#5a6478", border: "1px solid #DEE2E6",
  borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
};
const input = {
  width: "100%", padding: "6px 8px", fontSize: 12, color: "#1f2a3b",
  border: "1px solid #c9d6e8", borderRadius: 4, background: "#fff", boxSizing: "border-box",
};
const errBox = {
  display: "flex", alignItems: "center", gap: 6, marginTop: 8,
  padding: "6px 10px", borderRadius: 4, fontSize: 12,
  background: "#fde8e8", border: "1px solid #f5a5a5", color: "#8b0000",
};
